import { Button } from '@hologram-dimension/button';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useSimDetails from 'api/sims/useSimDetails';
import useSimMostRecentSession from 'api/sims/useSimMostRecentSession';
import { EmbeddedReport } from 'components';
import useIsDeactivatedOldSim from 'hooks/SimDetails/useIsDeactivatedOldSim';
import useSimBillingPeriod from 'hooks/SimDetails/useSimBillingPeriod';
import { type FC } from 'react';

interface SimUsageProps {
  hologramSimId?: HologramSimId;
}

const SimUsage: FC<SimUsageProps> = ({ hologramSimId }) => {
  const {
    simDetails,
    isLoading: isLoadingSimDetails,
    error: simDetailsError,
  } = useSimDetails(hologramSimId);

  const {
    simBillingPeriod,
    isLoading: isLoadingSimBillingPeriod,
    error: simBillingPeriodError,
  } = useSimBillingPeriod({ hologramSimId });

  const { billingPeriodStartMs, billingPeriodEndMs } = simBillingPeriod;

  const {
    mostRecentSession,
    error: mostRecentSessionError,
    isLoading: mostRecentSessionIsLoading,
  } = useSimMostRecentSession(hologramSimId);

  const {
    isDeactivatedOldSim,
    isLoading: deactivatedOldSimLoading,
    error: deactivatedOldSimError,
  } = useIsDeactivatedOldSim({
    hologramSimId,
  });

  const isLoading =
    isLoadingSimDetails ||
    isLoadingSimBillingPeriod ||
    mostRecentSessionIsLoading ||
    deactivatedOldSimLoading;

  const error =
    simDetailsError || simBillingPeriodError || mostRecentSessionError || deactivatedOldSimError;

  const failedToFindSimCardId = !isLoading && !simDetails.simCardId;
  const failedToDetermineBillingPeriod = !isLoading && !billingPeriodStartMs && !billingPeriodEndMs;

  if (error || failedToFindSimCardId || failedToDetermineBillingPeriod) {
    if (failedToFindSimCardId) {
      // eslint-disable-next-line no-console
      console.error('SIM Card ID not found');
    }

    if (failedToDetermineBillingPeriod) {
      // eslint-disable-next-line no-console
      console.error('Billing period not found');
    }

    return (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading the Device usage report"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        You can reload the page to try again
      </PanelNotification>
    );
  }

  if (isLoading || !simDetails.simCardId || !billingPeriodStartMs || !billingPeriodEndMs) {
    return <Loader />;
  }

  if (!mostRecentSession.sessionId) {
    if (isDeactivatedOldSim) {
      return (
        <PanelNotification icon="CircleAlert" label="No data usage available for this SIM">
          This SIM may have never connected and used data, or the usage data may be older than we
          store for deactivated SIMs.
        </PanelNotification>
      );
    }
    return (
      <PanelNotification icon="CircleAlert" label="This SIM hasn’t connected yet">
        This SIM is activated and able to connect to the Hologram network, but has not yet connected
        to a local tower or passed data.
      </PanelNotification>
    );
  }

  return (
    <EmbeddedReport
      reportId={239}
      simCardId={simDetails.simCardId}
      hologramSimId={hologramSimId}
      billingPeriodStart={billingPeriodStartMs}
      billingPeriodEnd={billingPeriodEndMs}
    />
  );
};

export default SimUsage;
